// client/src/components/payroll/PayrollUploadModal.jsx
import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Table, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Upload, FileSpreadsheet, Download,
  AlertTriangle, ShieldCheck, ArrowRight, Loader2
} from 'lucide-react';
import './PayrollUploadModal.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function PayrollUploadModal({ show, onHide, employees = [], onSuccess }) {
  const now = new Date();
  const fileInputRef = useRef(null);

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Map active employees by lowercase email
  const empMap = new Map();
  (employees || []).forEach(e => {
    if (e.email) empMap.set(e.email.toLowerCase().trim(), e);
  });

  const handleDownloadTemplate = (format = 'xlsx') => {
    // Generate template rows pre-populated with active team members
    const sampleRows = (employees.length > 0 ? employees : [
      { name: 'Jack Louis', email: 'jacklouis@platinumautojapan.com', role: 'Agent' },
      { name: 'Robert Jaffery', email: 'robert@platinumautojapan.com', role: 'Manager' },
      { name: 'Ahsan', email: 'ahsan@platinum.com', role: 'Manager' },
    ]).filter(e => e.role !== 'Admin').map(e => ({
      'Employee Email': e.email,
      'Month': month,
      'Year': year,
      'Base Salary (PKR)': 85000,
      'Commission (PKR)': 35000,
      'Bonuses (PKR)': 10000,
      'Deductions (PKR)': 0,
      'Cars Sold': 3,
      'Profit Generated (PKR)': 450000,
      'Payment Status (pending/paid)': 'pending',
      'Notes': 'Monthly target achievement',
    }));

    if (format === 'csv') {
      const csvStr = Papa.unparse(sampleRows);
      const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payroll_Template_${MONTH_NAMES[month - 1]}_${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.json_to_sheet(sampleRows);
      // Auto column widths
      ws['!cols'] = [
        { wch: 34 }, { wch: 8 }, { wch: 8 }, { wch: 18 }, { wch: 18 },
        { wch: 16 }, { wch: 18 }, { wch: 10 }, { wch: 22 }, { wch: 26 }, { wch: 30 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payroll_Import');
      XLSX.writeFile(wb, `Payroll_Template_${MONTH_NAMES[month - 1]}_${year}.xlsx`);
    }

    toast.success(`Downloaded ${format.toUpperCase()} template`);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    processFile(selected);
  };

  const processFile = (f) => {
    setErrorMsg('');
    setFile(f);
    setParsing(true);

    const ext = f.name.split('.').pop().toLowerCase();

    if (ext === 'csv') {
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          normalizeRows(results.data);
          setParsing(false);
        },
        error: (err) => {
          setErrorMsg('Failed to parse CSV file: ' + err.message);
          setParsing(false);
        }
      });
    } else if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const data = new Uint8Array(evt.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
          normalizeRows(json);
        } catch (err) {
          setErrorMsg('Failed to parse Excel file: ' + err.message);
        } finally {
          setParsing(false);
        }
      };
      reader.onerror = () => {
        setErrorMsg('Error reading file');
        setParsing(false);
      };
      reader.readAsArrayBuffer(f);
    } else {
      setErrorMsg('Unsupported format. Please upload an .xlsx, .xls, or .csv file.');
      setParsing(false);
    }
  };

  const normalizeRows = (raw) => {
    if (!Array.isArray(raw) || raw.length === 0) {
      setErrorMsg('The uploaded file is empty or has no readable rows.');
      setParsedRows([]);
      return;
    }

    const rows = raw.map((r, idx) => {
      const email = (r['Employee Email'] || r.email || r.Email || r.employee_email || '').toString().trim();
      const m = Number(r.Month || r.month || month);
      const y = Number(r.Year || r.year || year);
      const fixedSal = Number(r['Base Salary (PKR)'] || r['Base Salary'] || r.fixedSalary || r.salary || 0);
      const comm = Number(r['Commission (PKR)'] || r['Commission'] || r.commission || 0);
      const bon = Number(r['Bonuses (PKR)'] || r['Bonuses'] || r.bonuses || r.bonus || 0);
      const ded = Number(r['Deductions (PKR)'] || r['Deductions'] || r.deductions || r.deduction || 0);
      const cars = parseInt(r['Cars Sold'] || r.carsSold || 0, 10);
      const profit = Number(r['Profit Generated (PKR)'] || r['Profit Generated'] || r.profitGenerated || 0);
      const status = (r['Payment Status (pending/paid)'] || r['Payment Status'] || r.status || 'pending').toString().toLowerCase().trim();
      const notes = (r['Notes'] || r.notes || r.memo || '').toString().trim();

      const matchedEmp = empMap.get(email.toLowerCase());

      return {
        rowNum: idx + 1,
        email,
        matchedEmployee: matchedEmp,
        month: m || month,
        year: y || year,
        fixedSalary: isNaN(fixedSal) ? 0 : fixedSal,
        commission: isNaN(comm) ? 0 : comm,
        bonuses: isNaN(bon) ? 0 : bon,
        deductions: isNaN(ded) ? 0 : ded,
        netPay: (isNaN(fixedSal) ? 0 : fixedSal) + (isNaN(comm) ? 0 : comm) + (isNaN(bon) ? 0 : bon) - (isNaN(ded) ? 0 : ded),
        carsSold: isNaN(cars) ? 0 : cars,
        profitGenerated: isNaN(profit) ? 0 : profit,
        paymentStatus: status === 'paid' ? 'paid' : 'pending',
        notes
      };
    }).filter(r => r.email);

    if (rows.length === 0) {
      setErrorMsg('No rows with a valid "Employee Email" column found. Please review the template format.');
      setParsedRows([]);
    } else {
      setParsedRows(rows);
    }
  };

  const handleConfirmUpload = async () => {
    if (parsedRows.length === 0) {
      toast.error('No valid records to upload');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    try {
      const payload = {
        records: parsedRows,
        defaultMonth: month,
        defaultYear: year
      };

      const res = await api.post('/commissions/payroll/batch-upload', payload);

      toast.success(res.data.message || `Successfully updated ${res.data.updatedCount} payroll records.`);

      if (res.data.skippedCount > 0) {
        toast((t) => (
          <span>
            {res.data.skippedCount} record(s) were skipped due to unrecognized emails.
          </span>
        ), { icon: '⚠️' });
      }

      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      const errTxt = err.response?.data?.error || err.response?.data?.message || err.message || 'Batch update failed';
      setErrorMsg(errTxt);
      toast.error('Batch upload error: ' + errTxt);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedRows([]);
    setErrorMsg('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onHide();
  };

  const totalPayrollAmt = parsedRows.reduce((acc, r) => acc + (r.netPay || 0), 0);
  const matchedCount = parsedRows.filter(r => r.matchedEmployee).length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      dialogClassName="payroll-upload-modal-dialog"
      contentClassName="payroll-upload-modal-content"
    >
      <Modal.Header className="payroll-upload-header border-0 pb-0">
        <div className="d-flex align-items-center gap-3">
          <div className="payroll-upload-header-icon">
            <FileSpreadsheet size={22} />
          </div>
          <div>
            <h5 className="payroll-upload-title mb-0">Import Payroll via Excel / CSV</h5>
            <div className="payroll-upload-subtitle">
              Batch update employee salaries, commissions, bonuses, and deductions in one click
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn-close btn-close-white"
          onClick={handleClose}
          aria-label="Close"
        />
      </Modal.Header>

      <Modal.Body className="payroll-upload-body pt-3">
        {/* ── Top Period & Template Toolbar ── */}
        <div className="payroll-upload-toolbar">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-1">
              <span className="upload-period-lbl">Default Period:</span>
              <Form.Select
                size="sm"
                className="upload-select-input"
                style={{ width: 130 }}
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
              >
                {MONTH_NAMES.map((m, idx) => (
                  <option key={idx + 1} value={idx + 1}>{m}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                className="upload-select-input"
                style={{ width: 95 }}
                value={year}
                onChange={e => setYear(Number(e.target.value))}
              >
                {[now.getFullYear(), now.getFullYear() - 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </div>
          </div>

          {/* Download Sample Templates */}
          <div className="d-flex align-items-center gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleDownloadTemplate('xlsx')}
              className="d-flex align-items-center gap-1 template-download-btn"
            >
              <Download size={13} /> Sample Excel (.xlsx)
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleDownloadTemplate('csv')}
              className="d-flex align-items-center gap-1 template-download-btn"
            >
              <Download size={13} /> Sample CSV
            </Button>
          </div>
        </div>

        {errorMsg && (
          <Alert variant="danger" className="mt-3 py-2 px-3 small d-flex align-items-center gap-2">
            <AlertTriangle size={16} />
            <span>{errorMsg}</span>
          </Alert>
        )}

        {/* ── Drag & Drop Upload Zone ── */}
        <div
          className={`payroll-dropzone ${file ? 'has-file' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.files?.[0]) processFile(e.dataTransfer.files[0]);
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls, .csv"
            style={{ display: 'none' }}
          />

          <div className="dropzone-icon">
            <Upload size={24} />
          </div>

          <div>
            <div className="dropzone-title">
              {file ? file.name : 'Click to select or drag & drop payroll spreadsheet'}
            </div>
            <div className="dropzone-sub">
              {file ? `${(file.size / 1024).toFixed(1)} KB · Ready to preview & apply` : 'Supports Microsoft Excel (.xlsx, .xls) and Comma-Separated Values (.csv)'}
            </div>
          </div>

          {file && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="ms-auto"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setParsedRows([]);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Change File
            </Button>
          )}
        </div>

        {/* ── Parsed Preview Section ── */}
        {parsing && (
          <div className="text-center py-4 text-muted">
            <Loader2 size={24} className="crm-spinner me-2" /> Parsing spreadsheet columns...
          </div>
        )}

        {parsedRows.length > 0 && !parsing && (
          <div className="mt-4">
            {/* Summary Statistics Bar */}
            <div className="upload-stats-banner">
              <div className="d-flex align-items-center gap-3">
                <div className="upload-stat-item">
                  <span className="upload-stat-lbl">Parsed Employees:</span>
                  <span className="upload-stat-val text-white">{parsedRows.length}</span>
                </div>
                <div className="upload-stat-item">
                  <span className="upload-stat-lbl">CRM Matched:</span>
                  <span className="upload-stat-val text-success">{matchedCount} of {parsedRows.length}</span>
                </div>
                <div className="upload-stat-item">
                  <span className="upload-stat-lbl">Total Net Remuneration:</span>
                  <span className="upload-stat-val" style={{ color: '#38bdf8' }}>
                    PKR {totalPayrollAmt.toLocaleString()}
                  </span>
                </div>
              </div>

              <span className="badge rounded-pill" style={{ background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.25)', padding: '5px 12px' }}>
                <ShieldCheck size={12} className="me-1" /> Ready for Database Sync
              </span>
            </div>

            {/* Preview Table */}
            <div className="upload-table-wrapper mt-3">
              <Table responsive hover className="payroll-upload-table mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Employee</th>
                    <th>Period</th>
                    <th>Fixed Salary</th>
                    <th>Commission</th>
                    <th>Bonuses</th>
                    <th>Deductions</th>
                    <th>Net Payout</th>
                    <th>Cars</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((r) => (
                    <tr key={r.rowNum}>
                      <td className="text-muted">{r.rowNum}</td>
                      <td>
                        <div className="fw-semibold">
                          {r.matchedEmployee?.name || r.email}
                        </div>
                        <div className="small text-muted" style={{ fontSize: 11 }}>
                          {r.email}
                          {r.matchedEmployee ? (
                            <span className="text-success ms-1">✓ Matched ({r.matchedEmployee.role})</span>
                          ) : (
                            <span className="text-warning ms-1">⚠️ Unrecognized email</span>
                          )}
                        </div>
                      </td>
                      <td>{MONTH_NAMES[r.month - 1]?.slice(0, 3)} {r.year}</td>
                      <td>PKR {r.fixedSalary.toLocaleString()}</td>
                      <td>PKR {r.commission.toLocaleString()}</td>
                      <td>PKR {r.bonuses.toLocaleString()}</td>
                      <td className="text-danger">{r.deductions > 0 ? `-PKR ${r.deductions.toLocaleString()}` : 'PKR 0'}</td>
                      <td className="fw-bold" style={{ color: 'var(--success, #22c55e)' }}>
                        PKR {r.netPay.toLocaleString()}
                      </td>
                      <td>{r.carsSold}</td>
                      <td>
                        <span className={`payroll-status-badge ${r.paymentStatus === 'paid' ? 'paid' : 'pending'}`}>
                          {r.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="payroll-upload-footer border-0">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          size="sm"
          disabled={parsedRows.length === 0 || submitting}
          onClick={handleConfirmUpload}
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 160, justifyContent: 'center' }}
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="crm-spinner" /> Updating Payroll...
            </>
          ) : (
            <>
              Apply &amp; Update System <ArrowRight size={14} />
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
